import * as qs from 'qs';
import { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { Action, ResourceType } from '../../../../typings/permissions';
import { RouterContext } from '../../../contexts/RouterContext';
import { window } from '../../../globals';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import Card from '../../generic/Card/Card';
import Tabs, { useTabs } from '../../generic/Tabs';
import type { TabProps } from '../../generic/Tabs/Tab';

import CurrentPlan from './currentPlan';
import InvoiceHistory from './invoiceHistory';
import PaymentMethod from './paymentMethod';
import Plans from './plans';
import { PlansProvider } from './plans/PlansContext';

import styles from './style.module.less';

const tabValues = ['Current Plan', 'Invoice History', 'Payment Method', 'Plans'] as const;
type Tab = typeof tabValues[number];

function getDefaultTabFromQuery(): string | undefined {
  const params = qs.parse(window.location.search.substring(1));
  const qTab = params?.tab as Tab;
  if (qTab && tabValues.includes(qTab)) {
    return qTab;
  }
  return 'Current Plan';
}

const clearAllParams = () => window.history.pushState({}, '', window.location.pathname);

const Subscription: VoidFunctionComponent = () => {
  const hasPermission = useHasPermission();
  const hasAccessToBilling = hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationBillingSettings,
  });

  const tabIds: Tab[] = ['Current Plan'];
  const hasCustomBilling = window?.user?.organization?.custom_billing;
  const isOnPrem = window.on_prem;

  if (!hasCustomBilling && !isOnPrem) {
    tabIds.push('Invoice History', 'Payment Method', 'Plans');
  }
  const { getQuery, setQuery } = useContext(RouterContext);

  const {
    tabs,
    activeTab,
  } = useTabs({ defaultTab: getDefaultTabFromQuery(), tabIds });

  const shouldShowCurrentPlan = activeTab === 'Current Plan';
  const shouldShowInvoiceHistory = !hasCustomBilling && activeTab === 'Invoice History';
  const shouldShowPlans = !hasCustomBilling && activeTab === 'Plans';
  const shouldShowPaymentMethod = !hasCustomBilling && activeTab === 'Payment Method';

  const handleTabChange = (tab: TabProps) => {
    tab.onSelect();
    clearAllParams();
    setQuery({
      tab: tab.label,
    });
  };

  useEffect(() => {
    const queryParams = getQuery();
    const qTab = queryParams.tab as Tab;
    if (qTab && tabValues.includes(qTab)) {
      const targetTab = tabs.find((tab) => tab.label === qTab);
      if (targetTab && targetTab.label !== activeTab) {
        targetTab.onSelect();
      }
    }
  }, [activeTab, tabs, getQuery]);

  if (!hasAccessToBilling) {
    return null;
  }

  return (
    <div className="container margin-top">
      <Helmet title="Subscription" />
      <Card isFullWidth>
        <Card.Header>
          <h1 className={styles.header}>Subscription</h1>
        </Card.Header>
        <Card.Content>
          <Tabs>
            {tabs.map((tab) => (
              <Tabs.Tab
                key={tab.label}
                {...tab}
                onSelect={() => handleTabChange(tab)}
              />
            ))}
          </Tabs>

          {shouldShowCurrentPlan &&
            <PlansProvider>
              <CurrentPlan />
            </PlansProvider>}
          {shouldShowInvoiceHistory && <InvoiceHistory />}
          {shouldShowPaymentMethod && <PaymentMethod />}
          {shouldShowPlans &&
            <PlansProvider>
              <Plans />
            </PlansProvider>}
        </Card.Content>
      </Card>
    </div>
  );
};

export default Subscription;
